import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All Posts');
  const [blogPosts, setBlogPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  useEffect(() => {
    // Update page title and meta tags
    document.title = 'Blog | BusinessWebsitePro';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Web design tips, industry insights, and local guides for Vancouver, Toronto, Montreal, and Calgary businesses. Expert advice from BusinessWebsitePro.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'web design blog, SEO tips, digital marketing, website optimization, BC web design tips, Canada website advice, online business growth');
    }

    fetchBlogPosts();

    // Admin panel keyboard shortcut: Ctrl+Shift+A
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        setShowAdminPanel(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });

      if (error) throw error;

      setBlogPosts(data || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateBlogPost = async () => {
    try {
      setGenerating(true);
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/generate-blog-post`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
          }
        }
      );

      const result = await response.json();

      if (!response.ok) {
        console.error('Edge Function Error:', result);
        throw new Error(result.error || 'Failed to generate blog post');
      }

      if (!result.success) {
        throw new Error(result.error || 'Blog generation failed');
      }

      alert(`✅ Blog post generated successfully!\n\nTitle: ${result.blog_post.title}\nLocation: ${result.blog_post.location}\nAuthor: ${result.blog_post.author}\n\nTokens used: ${result.tokens_used}\nGeneration time: ${result.generation_time_seconds}s`);
      
      // Refresh blog posts
      await fetchBlogPosts();
    } catch (error) {
      console.error('Error generating blog post:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      alert(`❌ Failed to generate blog post\n\nError: ${errorMessage}\n\nPlease check:\n1. OpenAI API key is added to Supabase Edge Function secrets\n2. The secret name is exactly "OPENAI_API_KEY"\n3. Your OpenAI account has credits available`);
    } finally {
      setGenerating(false);
    }
  };

  const categories = ['All Posts', 'Web Design', 'Development', 'SEO Tips', 'Local SEO'];

  const filteredPosts = selectedCategory === 'All Posts' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const handlePostClick = (post: any) => {
    navigate(`/blog/${post.slug}`, { state: { post } });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hidden Admin Panel */}
      {showAdminPanel && (
        <div className="fixed top-20 right-4 z-50 bg-white rounded-2xl shadow-2xl border-2 border-gray-200 p-6 w-96">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Admin Panel</h3>
            <button
              onClick={() => setShowAdminPanel(false)}
              className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Generate AI-powered blog posts automatically
          </p>
          <button
            onClick={generateBlogPost}
            disabled={generating}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Generating...
              </>
            ) : (
              <>
                <i className="ri-magic-line mr-2"></i>
                Generate AI Blog Post
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 mt-3 text-center">
            Press Ctrl+Shift+A to toggle this panel
          </p>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative overflow-hidden" style={{ height: '75vh', minHeight: '75vh', maxHeight: '75vh' }}>
        {/* Hero Background Image */}
        <div className="absolute inset-0" style={{ height: '75vh' }}>
          <img 
            src="https://static.readdy.ai/image/afbb870fba5009b6251518206686e578/283520ad6e537ade2d8d407d8b9bb5ca.jpeg"
            alt="Blog - Web Design Tips and Insights"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center', height: '75vh', width: '100%', objectFit: 'cover' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
        </div>

        {/* No Upfront Payment Ribbon */}
        <div className="absolute top-24 right-8 z-20 animate-pulse">
          <div className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white px-8 py-4 rounded-xl shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-300">
            <div className="flex items-center gap-3">
              <i className="ri-gift-line text-3xl"></i>
              <div>
                <p className="text-sm font-bold uppercase tracking-wider">No Upfront Payment</p>
                <p className="text-xs opacity-90">Try Before You Pay</p>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-4 py-2 rounded-full mb-6">
                OUR BLOG
              </span>
              <h2 className="text-5xl lg:text-7xl font-bold text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Web Design Insights &amp; Tips
              </h2>
              <p className="text-xl lg:text-2xl text-white/90 mb-8 leading-relaxed" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Stay ahead with expert strategies, industry trends, and actionable guides to elevate your online presence and grow your business
              </p>
              <div className="flex items-center justify-center gap-4">
                <button 
                  onClick={() => document.querySelector('#blog-posts')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-white text-[#0d3b66] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Explore Articles
                </button>
                <button 
                  onClick={() => document.querySelector('#newsletter')?.scrollIntoView({ behavior: 'smooth' })}
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-[#0d3b66] transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Subscribe Now
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {blogPosts.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="bg-gradient-to-br from-[#0d3b66] to-[#0a2d4d] rounded-3xl overflow-hidden shadow-2xl">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-96 lg:h-auto">
                  <img 
                    src={blogPosts[0].featured_image_url || "https://public.readdy.ai/ai/img_res/fc166d5c12be95c27ecbb98491a3ec51.jpg"}
                    alt={blogPosts[0].title}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-12 lg:p-16 flex flex-col justify-center">
                  <span className="inline-block bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full mb-4 w-fit">
                    FEATURED POST
                  </span>
                  <h2 className="text-4xl font-bold text-white mb-6">
                    {blogPosts[0].title}
                  </h2>
                  <p className="text-lg text-white/90 mb-8">
                    {blogPosts[0].excerpt}
                  </p>
                  <div className="flex items-center gap-6 text-white/80 text-sm mb-8">
                    <span className="flex items-center gap-2">
                      <i className="ri-calendar-line"></i>
                      {new Date(blogPosts[0].published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-time-line"></i>
                      {blogPosts[0].read_time}
                    </span>
                  </div>
                  <button 
                    onClick={() => handlePostClick(blogPosts[0])}
                    className="bg-white text-[#0d3b66] px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 w-fit cursor-pointer whitespace-nowrap"
                  >
                    Read Article
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Category Filter */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  selectedCategory === category
                    ? 'bg-[#0d3b66] text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section id="blog-posts" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="text-center py-16">
              <i className="ri-loader-4-line text-6xl text-[#0d3b66] animate-spin mb-4"></i>
              <p className="text-xl text-gray-500">Loading blog posts...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
                {filteredPosts.map((post) => (
                  <article
                    key={post.id}
                    onClick={() => handlePostClick(post)}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                  >
                    <div className="w-full h-56 bg-gray-100 overflow-hidden relative">
                      <img
                        src={post.featured_image_url}
                        alt={post.title}
                        className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          console.error('Image failed to load:', post.featured_image_url);
                          e.currentTarget.src = `https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop&q=80`;
                        }}
                        loading="lazy"
                      />
                    </div>
                    <div className="p-8">
                      <div className="flex items-center justify-between mb-4">
                        <span className="inline-block bg-blue-100 text-[#0d3b66] text-xs font-semibold px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                        <span className="text-sm text-gray-500">{post.read_time}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#0d3b66] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <i className="ri-user-line text-gray-500"></i>
                          <span className="text-sm text-gray-600">{post.author_name}</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(post.published_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {filteredPosts.length === 0 && !loading && (
                <div className="text-center py-16">
                  <i className="ri-file-list-3-line text-6xl text-gray-300 mb-4"></i>
                  <p className="text-xl text-gray-500">No posts found in this category</p>
                  <p className="text-sm text-gray-400 mt-2">Try generating your first AI blog post using the button above!</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section id="newsletter" className="py-24 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-xl border border-gray-200">
            <div className="w-16 h-16 bg-[#0d3b66] rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-line text-3xl text-white"></i>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Subscribe to Our Newsletter</h2>
            <p className="text-lg text-gray-600 mb-8">
              Get the latest web design tips, trends, and insights delivered to your inbox
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-xl border-2 border-gray-200 focus:border-[#0d3b66] focus:outline-none text-base"
              />
              <button
                type="submit"
                className="bg-[#0d3b66] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#0a2d4d] transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
            <p className="text-sm text-gray-500 mt-4">No spam, unsubscribe anytime</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
